import React, { useState, useEffect } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCommentAltSmile } from '@fortawesome/pro-solid-svg-icons'
import { useTranslation } from 'react-i18next'

import { useSelector, useDispatch } from 'react-redux'
import { IMainState } from 'store/states'
import * as fromActions from 'store/actions/chat.action'
import { sanitizeHtml } from 'helpers/sanitizeHtml'
import updateMessageReader from 'api/messageReader/update'
import { useReaderMessage } from 'contexts/ReaderMessageContext'
import { useAccess } from 'contexts/AccessContext'
import Loader from '../loader/Loader'
import ChatInput from './components/Input/ChatInput'
import ChatMessages from './components/messages/ChatMessages'
import styles from './Chat.module.scss'

interface IChat {
  onSend: any
  courseId: number
  conversationId?: number
  fromModal?: boolean
  fromMonitoring?: boolean
}

const Chat: React.FC<IChat> = ({
  onSend,
  courseId,
  fromModal = false,
  fromMonitoring = false,
  conversationId = 0,
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const [messagesList, setMessagesList] = useState([] as any)
  const [userData, setUserData] = useState({ avatar: undefined, id: 0 } as any)
  const { user } = useSelector(({ user }: IMainState) => user)
  const { can } = useAccess()
  const canManageMessages = can('manageMessages')
  const { courseMessages } = useSelector(({ chat }: IMainState) => chat)
  const { markAsRead } = useReaderMessage()

  useEffect(() => {
    if (conversationId) {
      dispatch({
        type: fromActions.GET_CHAT_CONVERSATION_MESSAGES,
        payload: { conversationId, identifier: user.identifier },
      })
    } else {
      dispatch({
        type: fromActions.GET_CHAT_MESSAGES,
        payload: { courseId, identifier: user.identifier },
      })
    }
  }, [conversationId, courseId, dispatch, user.identifier])

  useEffect(() => {
    if (conversationId)
      dispatch({
        type: fromActions.GET_CHAT_CONVERSATION_MESSAGES,
        payload: { conversationId, identifier: user.identifier },
      })
  }, [conversationId, dispatch, user.identifier])

  useEffect(() => {
    if (user.identifier && fromModal && !fromMonitoring) {
      messagesList.length > 0 &&
        dispatch({ type: fromActions.GET_UNREAD_MESSAGES_COUNT })
      dispatch({
        type: fromActions.GET_CONVERSATIONS,
        payload: {
          identifier: user.identifier,
          loggedUserIsAdmin: canManageMessages,
        },
      })
    }
    if (!fromModal)
      dispatch({
        type: fromActions.GET_COURSE_UNREAD_MESSAGES_COUNT,
        payload: courseId,
      })
  }, [
    courseId,
    dispatch,
    fromModal,
    fromMonitoring,
    messagesList,
    canManageMessages,
    user.identifier,
  ])

  useEffect(() => {
    if (user) setUserData({ avatar: user.avatar, id: user.id })
  }, [user])

  useEffect(() => {
    if (courseMessages && courseMessages.conversationId) {
      setMessagesList(courseMessages.messages)
    }
  }, [courseMessages])

  const handleSend = (data: any) => {
    onSend({
      ...data,
      courseId,
      userId: courseMessages.to.id ? courseMessages.to.id : data.userId,
      conversationId,
    })
  }

  const handleDelete = (messageId: number) => {
    dispatch({
      type: fromActions.DESTROY_CHAT_MESSAGE,
      payload: {
        courseId,
        messageId,
        conversationId,
        identifier: user.identifier,
      },
    })
  }

  const readMessageReader = async () => {
    return await updateMessageReader(conversationId)
  }

  // Se separó ya que generaba una recarga de el otro selector
  const { loading } = useSelector(({ chat }: IMainState) => chat)

  useEffect(() => {
    const fetchMessagesReaded = async () => {
      const messagesReaded = await readMessageReader()
      markAsRead(messagesReaded)
    }
    if (conversationId) {
      fetchMessagesReaded()
    }
  }, [conversationId])

  return (
    <div className={`w-100 h-100 position-relative ${styles.chatRoot}`}>
      {!fromModal && (
        <h1 className={styles.title}>{t('components.chat.AskAnAdmin')}</h1>
      )}
      {loading ? (
        <div className={`${styles.messagesArea} ${styles.loading} chatLoading`}>
          <Loader color />
        </div>
      ) : courseMessages &&
        courseMessages.conversationId &&
        messagesList.length > 0 ? (
        <div className={styles.messagesArea}>
          <ChatMessages
            messages={messagesList}
            fromModal={fromModal}
            onDelete={handleDelete}
          />
        </div>
      ) : (
        <div className={styles.emptyList}>
          <FontAwesomeIcon
            className="mb-4"
            icon={faCommentAltSmile}
            size="4x"
          ></FontAwesomeIcon>
          <div
            className={styles.message}
            dangerouslySetInnerHTML={{
              __html: sanitizeHtml(
                t(
                  `components.chat.${
                    canManageMessages
                      ? 'emptyMessageListAdmin'
                      : 'emptyMessageListUser'
                  }`
                )
              ),
            }}
          />
        </div>
      )}
      <div className={styles.inputContainer}>
        <div className={`chatInput ${styles.input}`}>
          <ChatInput
            onSend={handleSend}
            userData={userData}
            isAdmin={canManageMessages}
          />
        </div>
      </div>
    </div>
  )
}

export default Chat
